const i18n = window.pricingCalculatorData?.i18n || {};

/**
 * Formats a number as a rounded US-style price fragment.
 *
 * @param {number} value Number to format.
 * @return {string} Formatted number.
 */
const formatPrice = ( value ) => Math.round( value ).toLocaleString( 'en-US' );

/**
 * Replaces `{key}` placeholders in a template string with provided values.
 *
 * @param {string} template Template string, e.g. "or {price}/year — save up to {pct}%".
 * @param {Object} vars     Map of placeholder names to replacement values.
 * @return {string} Interpolated string.
 */
const interpolate = ( template, vars ) =>
	template.replace( /\{(\w+)\}/g, ( _, key ) =>
		Object.prototype.hasOwnProperty.call( vars, key ) ? String( vars[ key ] ) : `{${ key }}`
	);

/**
 * Renders the main pricing amount.
 *
 * @param {HTMLElement|null} priceAmount Price amount element.
 * @param {number} subtotal Pricing subtotal.
 * @return {void}
 */
const renderMainPrice = ( priceAmount, subtotal ) => {

	if ( ! priceAmount ) {

		return;

	}

	priceAmount.textContent = formatPrice( subtotal );

};

/**
 * Renders the billing period label.
 *
 * @param {HTMLElement|null} pricePeriod Price period element.
 * @param {string} term Current billing term.
 * @param {Object} i18n Localized frontend strings.
 * @return {void}
 */
const renderPricePeriod = ( pricePeriod, term, i18n ) => {

	if ( ! pricePeriod ) {

		return;

	}

	pricePeriod.textContent = term === 'annual'
		? i18n.year || '/year'
		: i18n.month || '/month';

};

/**
 * Renders the savings/comparison message below the main price.
 *
 * - Annual selected: uses savingsAnnualTemplate with `{price}` placeholder.
 * - Monthly selected: uses savingsMonthlyTemplate with `{price}` and `{pct}` placeholders.
 *
 * @param {HTMLElement|null} priceSavings           Savings element.
 * @param {string}           term                   Current billing term ('annual' | 'monthly').
 * @param {number}           annualSubtotal          Annual pricing subtotal.
 * @param {number}           monthlySubtotal         Monthly pricing subtotal.
 * @param {Object}           templates               Text templates from block attributes.
 * @param {string}           templates.savingsAnnual Template for annual view (supports {price}).
 * @param {string}           templates.savingsMonthly Template for monthly view (supports {price}, {pct}).
 * @return {void}
 */
const renderSavings = ( priceSavings, term, annualSubtotal, monthlySubtotal, templates ) => {

	if ( ! priceSavings ) {

		return;

	}

	priceSavings.textContent = '';

	if ( term === 'annual' ) {

		if ( ! templates.savingsAnnual || annualSubtotal <= 0 ) {

			return;

		}

		priceSavings.textContent = interpolate( templates.savingsAnnual, {
			price: formatPrice( annualSubtotal / 12 ),
		} );

	} else {

		if ( ! templates.savingsMonthly || annualSubtotal <= 0 || monthlySubtotal <= 0 ) {

			return;

		}

		const monthlyAnnualized = monthlySubtotal * 12;

		if ( monthlyAnnualized <= annualSubtotal ) {

			return;

		}

		const savingsPct = Math.round( ( monthlyAnnualized - annualSubtotal ) / monthlyAnnualized * 100 );

		priceSavings.textContent = interpolate( templates.savingsMonthly, {
			price: formatPrice( annualSubtotal ),
			pct:   savingsPct,
		} );

	}

};

/**
 * Creates one bundle breakdown row.
 *
 * @param {Object} item Pricing item.
 * @param {string} term Current billing term.
 * @param {Object} i18n Localized frontend strings.
 * @return {HTMLElement} Breakdown item element.
 */
const createBreakdownItem = ( item, term, i18n ) => {

	const name = String( item.name || '' );
	const normalizedName = name.toLowerCase();
	const quantity = Number.parseInt( item.quantity, 10 ) || 0;
	const total = Number.parseFloat( item.total ) || 0;
	const dotColor = normalizedName === 'core'
		? '#066066'
		: normalizedName === 'extended'
			? '#7c3aed'
			: '#4f46e5';
	const periodLabel = term === 'annual' ? '/yr' : '/mo';
	const seatsLabel = i18n.seats || 'seats';

	const row = document.createElement( 'div' );
	row.className = 'b-pricing-calculator__breakdown-item';

	const dot = document.createElement( 'span' );
	dot.className = 'b-pricing-calculator__breakdown-dot';
	dot.style.background = dotColor;
	dot.setAttribute( 'aria-hidden', 'true' );

	const text = document.createTextNode(
		`${ name } (${ formatPrice( quantity ) } ${ seatsLabel }) — $${ formatPrice( total ) }${ periodLabel }`
	);

	row.appendChild( dot );
	row.appendChild( text );

	return row;

};

/**
 * Renders the bundle pricing breakdown.
 *
 * @param {HTMLElement|null} breakdown Breakdown container.
 * @param {Array<Object>} items Pricing items.
 * @param {string} term Current billing term.
 * @param {Object} i18n Localized frontend strings.
 * @param {NodeListOf<HTMLElement>|HTMLElement[]|null} planControls Plan controls in active panel.
 * @return {void}
 */
const renderBreakdown = ( breakdown, items, term, i18n, planControls = [] ) => {

	const controls = Array.from( planControls );
	const isMultiPlan = controls.length > 1;

	if ( isMultiPlan ) {

		controls.forEach( ( control ) => {

			const planKey = String( control.dataset.planKey || '' ).toLowerCase();
			const planBreakdown = control.querySelector( '[data-role="plan-breakdown"]' );

			if ( ! planBreakdown ) {

				return;

			}

			planBreakdown.innerHTML = '';

			const item = items.find(
				( entry ) => String( entry?.name || '' ).toLowerCase() === planKey
			);

			if ( ! item ) {

				planBreakdown.setAttribute( 'hidden', '' );
				return;

			}

			const row = createBreakdownItem( item, term, i18n );
			row.classList.add( 'b-pricing-calculator__breakdown-item--plan' );
			planBreakdown.appendChild( row );
			planBreakdown.removeAttribute( 'hidden' );

		} );

		if ( breakdown ) {

			breakdown.innerHTML = '';
			breakdown.setAttribute( 'hidden', '' );

		}

		return;

	}

	if ( breakdown ) {

		breakdown.innerHTML = '';
		breakdown.setAttribute( 'hidden', '' );

	}

};

/**
 * Checks if subtotal matches CTA visibility rule.
 *
 * @param {number} subtotal Pricing subtotal.
 * @param {string} operator Comparison operator.
 * @param {number} threshold Threshold amount.
 * @return {boolean} Whether CTA should be visible.
 */
const isRuleMatch = ( subtotal, operator, threshold ) => {

	switch ( operator ) {

	case '>':
		return subtotal > threshold;
	case '<':
		return subtotal < threshold;
	case '>=':
		return subtotal >= threshold;
	case '<=':
		return subtotal <= threshold;
	default:
		return false;

	}

};

/**
 * Updates visible CTA blocks based on subtotal and each block rule.
 *
 * @param {NodeListOf<HTMLElement>|HTMLElement[]|null} ctaBlocks CTA elements in active tab.
 * @param {number} subtotal Pricing subtotal.
 * @return {string} Active visible zone for current panel.
 */
const updateCta = ( ctaBlocks, subtotal ) => {

	if ( ! ctaBlocks || ctaBlocks.length === 0 ) {

		return '';

	}

	let activeZone = '';

	[ ...ctaBlocks ].forEach( ( ctaBlock ) => {

		const operator = ctaBlock.dataset.ctaOperator || '<';
		const threshold = Number.parseFloat( ctaBlock.dataset.ctaThreshold || '' );
		const isVisible = Number.isFinite( threshold ) && isRuleMatch( subtotal, operator, threshold );

		ctaBlock.toggleAttribute( 'hidden', ! isVisible );
		if ( isVisible && ! activeZone ) {

			activeZone = `${ ctaBlock.dataset.ctaZone || '' }`.trim();

		}

	} );

	return activeZone;

};

/**
 * Shows or hides price display depending on active CTA zone.
 *
 * Price is hidden for the "sales" zone so that only the CTA remains visible
 * once the high-price threshold is reached.
 *
 * @param {HTMLElement|null} priceDisplay Price display wrapper.
 * @param {string} activeZone Active visible CTA zone.
 * @return {void}
 */
const updatePriceDisplayVisibility = ( priceDisplay, activeZone ) => {

	if ( ! priceDisplay ) {

		return;

	}

	priceDisplay.toggleAttribute( 'hidden', activeZone === 'sales' );

};

/**
 * Shows or hides per-plan breakdown rows depending on active CTA zone.
 *
 * Breakdown rows expose per-plan amounts, so they follow the same rule as the
 * price display and stay hidden for the "sales" zone.
 *
 * @param {NodeListOf<HTMLElement>|HTMLElement[]|null} planControls Plan controls in active panel.
 * @param {string} activeZone Active visible CTA zone.
 * @return {void}
 */
const updatePlanBreakdownVisibility = ( planControls, activeZone ) => {

	const isHidden = activeZone === 'sales';

	[ ...( planControls || [] ) ].forEach( ( control ) => {

		const planBreakdown = control?.querySelector( '[data-role="plan-breakdown"]' );

		if ( ! planBreakdown ) {

			return;

		}

		if ( ! isHidden && planBreakdown.innerHTML === '' ) {

			return;

		}

		planBreakdown.toggleAttribute( 'hidden', isHidden );

	} );

};

/**
 * Returns seat values for supported plans in current panel.
 *
 * @param {NodeListOf<HTMLElement>|HTMLElement[]|null} planControls Plan control nodes in current panel.
 * @return {{ coreSeats: number, ultimateSeats: number }} Seats map.
 */
const getSeatValues = ( planControls ) => {

	const seats = {
		coreSeats: 0,
		ultimateSeats: 0,
	};

	[ ...( planControls || [] ) ].forEach( ( control ) => {

		const planKey = String( control?.dataset?.planKey || '' ).trim().toLowerCase();
		const value = Number.parseInt(
			control.querySelector( '[data-role="seat-input"]' )?.value || '',
			10
		);
		const normalizedValue = Number.isFinite( value ) ? Math.max( 0, value ) : 0;

		if ( planKey === 'core' ) {

			seats.coreSeats = normalizedValue;

		}

		if ( planKey === 'ultimate' ) {

			seats.ultimateSeats = normalizedValue;

		}

	} );

	return seats;

};

/**
 * Returns plan tier for dynamic CTA attributes.
 *
 * @param {string} panelPlanTier Explicit plan tier from panel data-plan-tier.
 * @param {string} panelPlans Plans list from panel data-plans.
 * @return {string} Plan tier.
 */
const getPlanTier = ( panelPlanTier, panelPlans ) => {

	const explicitTier = `${ panelPlanTier || '' }`.trim();
	if ( explicitTier ) {

		return explicitTier;

	}

	return String( panelPlans || '' )
		.split( ',' )
		.map( ( value ) => value.trim().toLowerCase() )
		.filter( Boolean )
		.join( '+' );

};

/**
 * Updates CTA button links by appending dynamic calculator params.
 *
 * @param {Object} options Link update options.
 * @param {NodeListOf<HTMLElement>|HTMLElement[]|null} options.ctaBlocks CTA blocks in current panel.
 * @param {string} options.panelPlanTier Current panel explicit tier label from data-plan-tier.
 * @param {string} options.panelPlans Current panel plans list from data-plans.
 * @param {number} options.subtotal Current subtotal value.
 * @param {string} options.term Current billing term ('annual' | 'monthly').
 * @param {{ coreSeats: number, ultimateSeats: number }} options.seats Seats map.
 * @return {void}
 */
const updateCtaDynamicLinks = ( {
	ctaBlocks,
	panelPlanTier,
	panelPlans,
	subtotal,
	term,
	seats,
} ) => {

	if ( ! ctaBlocks || ctaBlocks.length === 0 ) {

		return;

	}

	const planTier = getPlanTier( panelPlanTier, panelPlans );
	const value = Math.max( 0, Math.round( Number( subtotal ) || 0 ) );
	const coreSeats = Math.max( 0, Math.round( Number( seats?.coreSeats ) || 0 ) );
	const ultimateSeats = Math.max( 0, Math.round( Number( seats?.ultimateSeats ) || 0 ) );
	const totalSeats = coreSeats + ultimateSeats;

	[ ...ctaBlocks ].forEach( ( ctaBlock ) => {

		if ( ctaBlock.dataset.ctaDynamicAttrs !== 'true' ) {

			return;

		}

		ctaBlock.querySelectorAll( '.wp-block-button__link' ).forEach( ( buttonLink ) => {

			const href = String( buttonLink.getAttribute( 'href' ) || '' ).trim();

			if ( ! href ) {

				return;

			}

			let url = null;

			try {

				url = new URL( href, window.location.href );

			} catch ( error ) {

				return;

			}

			if ( url.protocol !== 'http:' && url.protocol !== 'https:' ) {

				return;

			}

			url.searchParams.set( 'plan_tier', planTier );
			url.searchParams.set( 'core_seats', String( coreSeats ) );
			url.searchParams.set( 'ultimate_seats', String( ultimateSeats ) );
			url.searchParams.set( 'value', String( value ) );
			url.searchParams.set( 'billing_term', term );
			url.searchParams.set( 'seats', String( totalSeats ) );

			buttonLink.setAttribute( 'href', url.toString() );
			buttonLink.setAttribute( 'data-pricing-plan-tier', planTier );
			buttonLink.setAttribute( 'data-pricing-core-seats', String( coreSeats ) );
			buttonLink.setAttribute( 'data-pricing-ultimate-seats', String( ultimateSeats ) );
			buttonLink.setAttribute( 'data-pricing-value', String( value ) );
			buttonLink.setAttribute( 'data-pricing-billing-term', term );
			buttonLink.setAttribute( 'data-pricing-seats', String( totalSeats ) );

		} );

	} );

};

/**
 * Updates Amplitude data-* attributes on CTA buttons with enabled event tracking.
 *
 * Runs only for CTA blocks that have dynamic Amplitude attributes enabled and
 * only for button links that already define `data-amplitude-event-name`.
 *
 * @param {Object} options Link update options.
 * @param {NodeListOf<HTMLElement>|HTMLElement[]|null} options.ctaBlocks CTA blocks in current panel.
 * @param {string} options.panelPlanTier Current panel explicit tier label from data-plan-tier.
 * @param {string} options.panelPlans Current panel plans list from data-plans.
 * @param {number} options.subtotal Current subtotal value.
 * @param {string} options.term Current billing term ('annual' | 'monthly').
 * @param {{ coreSeats: number, ultimateSeats: number }} options.seats Seats map.
 * @return {void}
 */
const updateCtaDynamicAmplitudeAttributes = ( {
	ctaBlocks,
	panelPlanTier,
	panelPlans,
	subtotal,
	term,
	seats,
} ) => {

	if ( ! ctaBlocks || ctaBlocks.length === 0 ) {

		return;

	}

	const planTier = getPlanTier( panelPlanTier, panelPlans );
	const value = Math.max( 0, Math.round( Number( subtotal ) || 0 ) );
	const coreSeats = Math.max( 0, Math.round( Number( seats?.coreSeats ) || 0 ) );
	const ultimateSeats = Math.max( 0, Math.round( Number( seats?.ultimateSeats ) || 0 ) );
	const totalSeats = coreSeats + ultimateSeats;

	[ ...ctaBlocks ].forEach( ( ctaBlock ) => {

		if ( ctaBlock.dataset.ctaDynamicAmplitudeAttrs !== 'true' ) {

			return;

		}

		ctaBlock.querySelectorAll( '.wp-block-button__link[data-amplitude-event-name]' ).forEach( ( buttonLink ) => {

			const eventName = String( buttonLink.getAttribute( 'data-amplitude-event-name' ) || '' ).trim();
			if ( ! eventName ) {

				return;

			}

			buttonLink.setAttribute( 'data-amplitude-plan-tier', planTier );
			buttonLink.setAttribute( 'data-amplitude-core-seats', String( coreSeats ) );
			buttonLink.setAttribute( 'data-amplitude-ultimate-seats', String( ultimateSeats ) );
			buttonLink.setAttribute( 'data-amplitude-value', String( value ) );
			buttonLink.setAttribute( 'data-amplitude-billing-term', term );
			buttonLink.setAttribute( 'data-amplitude-seats', String( totalSeats ) );

		} );

	} );

};

/**
 * Hides the pricing error message.
 *
 * @param {HTMLElement|null} errorMessage Error message element.
 * @return {void}
 */
const hidePricingError = ( errorMessage ) => {

	if ( ! errorMessage ) {

		return;

	}

	errorMessage.textContent = '';
	errorMessage.setAttribute( 'hidden', '' );

};

/**
 * Renders the pricing request error message.
 *
 * @param {Object} options Rendering options.
 * @param {Object} options.elements DOM elements used by the renderer.
 * @param {Object} options.i18n Localized frontend strings.
 * @return {void}
 */
export const renderPricingError = ( { elements, i18n } ) => {

	if ( elements.priceDisplay ) {

		elements.priceDisplay.removeAttribute( 'hidden' );

	}

	if ( ! elements.errorMessage ) {

		return;

	}

	elements.errorMessage.textContent = i18n.requestError || 'We could not calculate the price. Please try again later.';
	elements.errorMessage.removeAttribute( 'hidden' );

};

/**
 * Renders pricing data returned by the API.
 *
 * @param {Object} options                Rendering options.
 * @param {Object} options.annualData     API response for annual term.
 * @param {Object} options.monthlyData    API response for monthly term.
 * @param {Object} options.elements       DOM elements used by the renderer.
 * @param {string} options.term           Current billing term ('annual' | 'monthly').
 * @param {Object} options.templates      Text templates: savingsAnnual, savingsMonthly.
 * @param {Object} options.i18n           Localized frontend strings.
 * @return {{ subtotal: number, activeZone: string }} Render metadata.
 */
export const renderPricing = ( {
	annualData,
	monthlyData,
	elements,
	term,
	templates,
	i18n,
} ) => {

	const primaryPricing  = ( term === 'annual' ? annualData : monthlyData )?.pricing || {};
	const subtotal        = Number.parseFloat( primaryPricing.subtotal ) || 0;
	const items           = Array.isArray( primaryPricing.items ) ? primaryPricing.items : [];
	const seats           = getSeatValues( elements.planControls );

	const annualSubtotal  = Number.parseFloat( annualData?.pricing?.subtotal ) || 0;
	const monthlySubtotal = Number.parseFloat( monthlyData?.pricing?.subtotal ) || 0;

	hidePricingError( elements.errorMessage );
	renderMainPrice( elements.priceAmount, subtotal );
	renderPricePeriod( elements.pricePeriod, term, i18n );
	renderSavings( elements.priceSavings, term, annualSubtotal, monthlySubtotal, templates );
	renderBreakdown( elements.breakdown, items, term, i18n, elements.planControls );
	const activeZone = updateCta( elements.ctaBlocks, subtotal );
	updatePriceDisplayVisibility( elements.priceDisplay, activeZone );
	updatePlanBreakdownVisibility( elements.planControls, activeZone );
	updateCtaDynamicLinks( {
		ctaBlocks: elements.ctaBlocks,
		panelPlanTier: elements.panelPlanTier,
		panelPlans: elements.panelPlans,
		subtotal,
		term,
		seats,
	} );
	updateCtaDynamicAmplitudeAttributes( {
		ctaBlocks: elements.ctaBlocks,
		panelPlanTier: elements.panelPlanTier,
		panelPlans: elements.panelPlans,
		subtotal,
		term,
		seats,
	} );
	return {
		subtotal,
		activeZone,
	};

};
